import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, DollarSign, Lock, Unlock } from 'lucide-react';
import { masterSettingsAPI } from '../../api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const OtherFees = () => {
  const [fees, setFees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    course_id: '',
    semester_id: '',
    session_id: '',
    applicable_to: 'Both',
    amount: '',
    payment_date: '',
    late_fine: '',
    description: '',
    is_mandatory: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [feesRes, coursesRes, semestersRes] = await Promise.all([
        masterSettingsAPI.getOtherFees(),
        masterSettingsAPI.getCourses(),
        masterSettingsAPI.getSemesters()
      ]);
      setFees(feesRes.data);
      setCourses(coursesRes.data);
      setAllSemesters(semestersRes.data);
      
      // Fetch sessions if endpoint exists, otherwise set empty array
      try {
        const sessionsRes = await masterSettingsAPI.getSessions();
        setSessions(sessionsRes.data || []);
      } catch (err) {
        console.log('Sessions endpoint not available, setting empty array');
        setSessions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = async (courseId) => {
    setFormData({ ...formData, course_id: courseId, semester_id: '', session_id: '' });
    if (courseId) {
      const courseSemesters = allSemesters.filter(s => s.course_id === courseId);
      setSemesters(courseSemesters);
    } else {
      setSemesters([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingFee) {
        await masterSettingsAPI.updateOtherFee(editingFee.id, payload);
        alert('Fee updated successfully!');
      } else {
        await masterSettingsAPI.createOtherFee(payload);
        alert('Fee created successfully!');
      }
      fetchData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving fee:', error);
      alert('Failed to save fee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (fee) => {
    const action = fee.is_active ? 'block' : 'unblock';
    if (window.confirm(`Are you sure you want to ${action} this fee?`)) {
      setLoading(true);
      try {
        await masterSettingsAPI.updateOtherFee(fee.id, { ...fee, is_active: !fee.is_active });
        alert(`Fee ${action}ed successfully!`);
        fetchData();
      } catch (error) {
        console.error(`Error ${action}ing fee:`, error);
        alert(`Failed to ${action} fee. Please try again.`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this fee?')) {
      setLoading(true);
      try {
        await masterSettingsAPI.deleteOtherFee(id);
        alert('Fee deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Error deleting fee:', error);
        alert('Failed to delete fee. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = async (fee) => {
    setEditingFee(fee);
    
    // Load semesters for the course first
    if (fee.course_id) {
      const courseSemesters = allSemesters.filter(s => s.course_id === fee.course_id);
      setSemesters(courseSemesters);
    }
    
    // Then set all form data including semester_id and session_id
    setFormData({
      name: fee.name || '',
      course_id: fee.course_id || '',
      semester_id: fee.semester_id || '',
      session_id: fee.session_id || '',
      applicable_to: fee.applicable_to || 'Both',
      amount: fee.amount ? fee.amount.toString() : '',
      payment_date: fee.payment_date || '',
      late_fine: fee.late_fine ? fee.late_fine.toString() : '',
      description: fee.description || '',
      is_mandatory: fee.is_mandatory || false
    });
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFee(null);
    setSemesters([]);
    setFormData({
      name: '',
      course_id: '',
      semester_id: '',
      session_id: '',
      applicable_to: 'Both',
      amount: '',
      payment_date: '',
      late_fine: '',
      description: '',
      is_mandatory: false
    });
  };

  const filteredFees = fees.filter(fee =>
    fee.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Other Fees</h1>
          <p className="text-sm text-gray-500 mt-1">Manage additional fee types and charges</p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            setEditingFee(null);
            setSemesters([]);
            setFormData({ name: '', course_id: '', semester_id: '', session_id: '', applicable_to: 'Both', amount: '', description: '', is_mandatory: false });
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          disabled={loading}
        >
          <Plus className="h-4 w-4" />
          Add Other Fee
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Fees</p>
              <p className="text-2xl font-bold text-gray-900">{fees.length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Mandatory Fees</p>
              <p className="text-2xl font-bold text-green-600">
                {fees.filter(f => f.is_mandatory).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Optional Fees</p>
              <p className="text-2xl font-bold text-orange-600">
                {fees.filter(f => !f.is_mandatory).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search fees by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount (₹)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Late Fine (₹)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredFees.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                    No fees found. Click "Add Other Fee" to create one.
                  </td>
                </tr>
              ) : (
                filteredFees.map((fee) => {
                  const course = courses.find(c => c.id === fee.course_id);
                  const session = sessions.find(s => s.id === fee.session_id);
                  
                  return (
                  <tr key={fee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {fee.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {course ? course.name : 'All Courses'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {session ? session.name : 'All Sessions'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      ₹{fee.amount.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {fee.payment_date ? formatDate(fee.payment_date) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {fee.late_fine ? `₹${fee.late_fine.toLocaleString('en-IN')}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        fee.is_mandatory
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {fee.is_mandatory ? 'Mandatory' : 'Optional'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {fee.description || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        fee.is_active === false
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {fee.is_active === false ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm space-x-2 flex items-center">
                      <button
                        onClick={() => handleEdit(fee)}
                        className="text-blue-600 hover:text-blue-900"
                        disabled={loading}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(fee.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={loading}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(fee)}
                        className={`${
                          fee.is_active === false
                            ? 'text-green-600 hover:text-green-900'
                            : 'text-orange-600 hover:text-orange-900'
                        }`}
                        disabled={loading}
                        title={fee.is_active === false ? 'Unblock' : 'Block'}
                      >
                        {fee.is_active === false ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
            <h2 className="text-xl font-bold mb-4">
              {editingFee ? 'Edit Other Fee' : 'Add Other Fee'}
            </h2>
            <form onSubmit={handleSubmit}>
              {/* 2-Column Grid Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Library Fee, Sports Fee"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter amount"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date (Due Date) <span className="text-gray-400 text-xs">(Optional - Set when assigning to student)</span>
                  </label>
                  <input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Late Fine (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.late_fine}
                    onChange={(e) => setFormData({ ...formData, late_fine: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Fine amount if paid late"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course <span className="text-gray-400 text-xs">(Optional - Leave empty for all courses)</span>
                  </label>
                  <select
                    value={formData.course_id}
                    onChange={(e) => handleCourseChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Courses</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Semester <span className="text-gray-400 text-xs">(Optional - Leave empty for all semesters)</span>
                  </label>
                  <select
                    value={formData.semester_id}
                    onChange={(e) => setFormData({ ...formData, semester_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={!formData.course_id}
                  >
                    <option value="">All Semesters</option>
                    {semesters.map(semester => (
                      <option key={semester.id} value={semester.id}>{semester.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session
                  </label>
                  <select
                    value={formData.session_id}
                    onChange={(e) => setFormData({ ...formData, session_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Session (Optional)</option>
                    {sessions.map(session => (
                      <option key={session.id} value={session.id}>{session.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Applicable To <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.applicable_to}
                    onChange={(e) => setFormData({ ...formData, applicable_to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                  <option value="Both">Both (Regular & Lateral)</option>
                  <option value="Regular">Regular Students Only</option>
                  <option value="Lateral">Lateral Students Only</option>
                </select>
              </div>
              </div>

              {/* Full Width Fields */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                  placeholder="Brief description of the fee"
                />
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="is_mandatory"
                  checked={formData.is_mandatory}
                  onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_mandatory" className="ml-2 block text-sm text-gray-700">
                  Mark as mandatory fee (required for all students)
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingFee ? 'Update Fee' : 'Create Fee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherFees;
